import { useCallback, useEffect, useState } from 'react';
import type { Integration, IntegrationProvider, IntegrationStatus, IntegrationToolView } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

const INTEGRATION_PROVIDERS: readonly IntegrationProvider[] = [
  'slack',
  'gmail',
  'google_workspace',
  'microsoft_365',
  'github',
  'jira',
  'confluence',
  'salesforce',
  'hubspot',
  'notion',
  'http',
  'webhook',
  'sql',
  'sharepoint',
  'azure_devops',
  'teams',
  'email',
  'argocd',
  'grafana',
  'prometheus',
  'sentry',
];

interface ConfigField {
  key: string;
  label: string;
  placeholder: string;
}

interface SecretField {
  key: string;
  label: string;
  placeholder: string;
}

type IntegrationWithTools = Integration & {
  tools?: IntegrationToolView[];
  toolCount?: number;
  approvalRequiredCount?: number;
};

const BASE_URL_FIELD: ConfigField = {
  key: 'baseUrl',
  label: 'Workspace / API base URL',
  placeholder: 'https://your-domain.example.com',
};

const PROVIDER_CONFIG_FIELDS: Record<IntegrationProvider, ConfigField[]> = {
  slack: [],
  gmail: [],
  google_workspace: [],
  microsoft_365: [],
  github: [],
  jira: [BASE_URL_FIELD],
  confluence: [BASE_URL_FIELD],
  salesforce: [BASE_URL_FIELD],
  hubspot: [],
  notion: [],
  http: [BASE_URL_FIELD],
  webhook: [{ key: 'webhookUrl', label: 'Webhook URL', placeholder: 'https://hooks.example.com/...' }],
  sql: [],
  sharepoint: [],
  azure_devops: [{ key: 'organization', label: 'Organization', placeholder: 'acme' }],
  teams: [{ key: 'webhookUrl', label: 'Incoming webhook URL', placeholder: 'Optional if not using Graph credentials' }],
  email: [{ key: 'apiUrl', label: 'Email provider API URL', placeholder: 'https://email.example.com' }],
  argocd: [BASE_URL_FIELD],
  grafana: [BASE_URL_FIELD],
  prometheus: [BASE_URL_FIELD],
  sentry: [{ key: 'organizationSlug', label: 'Organization slug', placeholder: 'acme' }],
};

const DEFAULT_SECRET_FIELDS: SecretField[] = [
  { key: 'token', label: 'Access token / API key', placeholder: 'Paste the secret value' },
];

const PROVIDER_SECRET_FIELDS: Partial<Record<IntegrationProvider, SecretField[]>> = {
  sql: [
    {
      key: 'connectionString',
      label: 'Database connection string',
      placeholder: 'postgres://user:password@host:5432/db',
    },
  ],
};

function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 120);
}

function errorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof parsed.message === 'string') return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function statusVariant(status: IntegrationStatus): 'success' | 'destructive' | 'secondary' | 'warning' {
  switch (status) {
    case 'connected':
      return 'success';
    case 'error':
      return 'destructive';
    case 'disconnected':
      return 'secondary';
    case 'pending':
      return 'warning';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

function fieldsFor(provider: IntegrationProvider): ConfigField[] {
  return PROVIDER_CONFIG_FIELDS[provider] ?? [];
}

function secretFieldsFor(provider: IntegrationProvider): SecretField[] {
  return PROVIDER_SECRET_FIELDS[provider] ?? DEFAULT_SECRET_FIELDS;
}

function buildConfig(values: Record<string, string>): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    const trimmed = value.trim();
    if (trimmed) config[key] = trimmed;
  }
  return config;
}

function buildStringValues(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    const trimmed = value.trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

function hasSecretValues(values: Record<string, string>): boolean {
  return Object.values(values).some((value) => value.trim().length > 0);
}

export function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<IntegrationWithTools[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const [provider, setProvider] = useState<IntegrationProvider>(INTEGRATION_PROVIDERS[0]);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [editName, setEditName] = useState('');
  const [editConfigValues, setEditConfigValues] = useState<Record<string, string>>({});
  const [secretEditValues, setSecretEditValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const rows = await apiClient.listIntegrations();
    setIntegrations(rows as IntegrationWithTools[]);
  }, []);

  useEffect(() => {
    void load().catch((err: unknown) => setError(errorMessage(err)));
  }, [load]);

  function onProviderChange(next: IntegrationProvider): void {
    const label = titleCase(next);
    setProvider(next);
    setName(label);
    setKeyEdited(false);
    setKey(slugify(`${next}.${label}`));
    setConfigValues({});
    setSecretValues({});
  }

  function onNameChange(next: string): void {
    setName(next);
    if (!keyEdited) setKey(slugify(`${provider}.${next.trim() || provider}`));
  }

  async function create(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setError(null);
    const secrets = buildStringValues(secretValues);
    try {
      const created = await apiClient.createIntegration({
        provider,
        key: key.trim(),
        name: name.trim(),
        config: buildConfig(configValues),
      });
      if (Object.keys(secrets).length) {
        await apiClient.updateIntegrationSecrets(created.id, secrets);
      }
      setShowCreate(false);
      setName('');
      setKey('');
      setKeyEdited(false);
      setConfigValues({});
      setSecretValues({});
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function startConfigure(item: Integration): void {
    setConfiguring(item.id);
    setConnecting(null);
    setEditName(item.name);
    const cfg = (item.config ?? {}) as Record<string, unknown>;
    const next: Record<string, string> = {};
    for (const field of fieldsFor(item.provider)) {
      const value = cfg[field.key];
      next[field.key] = typeof value === 'string' ? value : '';
    }
    setEditConfigValues(next);
  }

  async function saveConfig(item: Integration): Promise<void> {
    setError(null);
    try {
      const existing = (item.config ?? {}) as Record<string, unknown>;
      const config = buildConfig(editConfigValues);
      if (existing['toolApprovalOverrides']) config['toolApprovalOverrides'] = existing['toolApprovalOverrides'];
      await apiClient.updateIntegration(item.id, {
        name: editName.trim() || item.name,
        config,
      });
      setConfiguring(null);
      setEditName('');
      setEditConfigValues({});
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function startConnect(item: Integration): void {
    setConnecting(item.id);
    setConfiguring(null);
    setSecretEditValues({});
  }

  async function confirmConnect(item: Integration): Promise<void> {
    setError(null);
    try {
      await apiClient.updateIntegrationSecrets(item.id, buildStringValues(secretEditValues));
      setConnecting(null);
      setSecretEditValues({});
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function disconnect(item: Integration): Promise<void> {
    setError(null);
    try {
      await apiClient.setIntegrationStatus(item.id, 'disconnected');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function remove(item: Integration): Promise<void> {
    setError(null);
    try {
      await apiClient.deleteIntegration(item.id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function setToolApproval(item: IntegrationWithTools, actionKey: string, requiresApproval: boolean): Promise<void> {
    const config = { ...((item.config ?? {}) as Record<string, unknown>) };
    const overrides = {
      ...((config['toolApprovalOverrides'] as Record<string, boolean> | undefined) ?? {}),
      [actionKey]: requiresApproval,
    };
    config['toolApprovalOverrides'] = overrides;
    setError(null);
    try {
      const updated = await apiClient.updateIntegration(item.id, { config });
      setIntegrations((list) =>
        list.map((row) => (row.id === item.id ? ({ ...row, ...updated } as IntegrationWithTools) : row)),
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function toggleTools(id: string): void {
    setExpandedTools((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const configFields = fieldsFor(provider);
  const secretFields = secretFieldsFor(provider);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Integrations</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Named connections. Pending tiles stay pending until a secret is saved. They are not live.
            The AP demo uses Mailbox and Payment orders as stand-ins for email and the payment system.
            SAP is not connected. Teams is not the HITL channel.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((open) => !open)}>Add integration</Button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      {showCreate ? (
        <Card className="mt-4 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Add integration</h2>
          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs text-gray-500">Provider</span>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              value={provider}
              onChange={(e) => onProviderChange(e.target.value as IntegrationProvider)}
            >
              {INTEGRATION_PROVIDERS.map((item) => (
                <option key={item} value={item}>{titleCase(item)}</option>
              ))}
            </select>
          </label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label>
              <span className="text-xs text-gray-500">Name</span>
              <Input className="mt-1" value={name} placeholder="Acme Slack" onChange={(e) => onNameChange(e.target.value)} />
            </label>
            <label>
              <span className="text-xs text-gray-500">Key</span>
              <Input
                className="mt-1"
                value={key}
                placeholder="slack.acme"
                onChange={(e) => {
                  setKeyEdited(true);
                  setKey(e.target.value);
                }}
              />
              <span className="mt-1 block text-xs text-gray-500">Skills require this stable integration key.</span>
            </label>
          </div>
          {configFields.length ? (
            <>
              <p className="mt-3 text-sm text-gray-500">
                Only connection-level details are needed here. API actions are discovered by the agent
                from the provider catalog after the integration is assigned.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {configFields.map((field) => (
                  <label key={field.key}>
                    <span className="text-xs text-gray-500">{field.label}</span>
                    <Input
                      className="mt-1"
                      placeholder={field.placeholder}
                      value={configValues[field.key] ?? ''}
                      onChange={(e) => setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
            </>
          ) : null}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {secretFields.map((field) => (
              <label key={field.key}>
                <span className="text-xs text-gray-500">{field.label}</span>
                <Input
                  className="mt-1"
                  type="password"
                  autoComplete="new-password"
                  placeholder={field.placeholder}
                  value={secretValues[field.key] ?? ''}
                  onChange={(e) => setSecretValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
                <span className="mt-1 block text-xs text-gray-500">Stored in Vault. The value is never shown again.</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" disabled={!name.trim() || !key.trim() || saving} onClick={() => void create()}>
              {saving ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4">
        {integrations.length === 0 ? <p className="text-sm text-gray-500">No integrations yet.</p> : null}
        {integrations.map((item) => {
          const tools = item.tools ?? [];
          const count = item.toolCount ?? tools.length;
          const gated = item.approvalRequiredCount ?? tools.filter((tool) => tool.requiresApproval).length;
          return (
            <Card key={item.id} className="p-4">
              <div className="flex justify-between gap-2">
                <strong className="text-gray-900 dark:text-white">{item.name}</strong>
                <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
              </div>
              <div className="mt-1 text-sm text-gray-500">
                {item.provider} · <code>{item.key}</code>
              </div>
              <div className="text-xs text-gray-500">
                {count} tools · {gated} require approval · Credentials apply to all tools automatically.
              </div>
              <Button size="sm" variant="ghost" className="mt-2 px-0" onClick={() => toggleTools(item.id)}>
                Tools
              </Button>
              {expandedTools.has(item.id) ? (
                <div className="max-h-64 space-y-2 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-950">
                  {tools.length === 0 ? <p className="text-xs text-gray-500">No tools in catalog for this provider.</p> : null}
                  {tools.map((tool) => (
                    <div key={tool.key} className="flex items-start justify-between gap-2 border-b border-gray-200 pb-2 last:border-0 dark:border-gray-800">
                      <div className="min-w-0">
                        <div className="text-xs font-medium">{tool.name}</div>
                        <div className="text-[11px] text-gray-500">{tool.description}</div>
                        <code className="text-[10px] text-gray-400">{tool.key}</code>
                      </div>
                      <label className="flex items-center gap-1 text-[10px] text-gray-500">
                        Approval
                        <input
                          type="checkbox"
                          checked={tool.requiresApproval}
                          onChange={(e) => void setToolApproval(item, tool.key, e.target.checked)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              ) : null}

              {configuring === item.id ? (
                <div className="mt-2 grid gap-2">
                  <label>
                    <span className="text-xs text-gray-500">Name</span>
                    <Input className="mt-1" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </label>
                  {fieldsFor(item.provider).map((field) => (
                    <label key={field.key}>
                      <span className="text-xs text-gray-500">{field.label}</span>
                      <Input
                        className="mt-1"
                        placeholder={field.placeholder}
                        value={editConfigValues[field.key] ?? ''}
                        onChange={(e) => setEditConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setConfiguring(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => void saveConfig(item)}>Save config</Button>
                  </div>
                </div>
              ) : connecting === item.id ? (
                <div className="mt-2 grid gap-2">
                  {secretFieldsFor(item.provider).map((field) => (
                    <label key={field.key}>
                      <span className="text-xs text-gray-500">{field.label}</span>
                      <Input
                        className="mt-1"
                        type="password"
                        autoComplete="new-password"
                        placeholder={field.placeholder}
                        value={secretEditValues[field.key] ?? ''}
                        onChange={(e) => setSecretEditValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setConnecting(null)}>Cancel</Button>
                    <Button size="sm" disabled={!hasSecretValues(secretEditValues)} onClick={() => void confirmConnect(item)}>
                      Save secret
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startConfigure(item)}>Configure</Button>
                  <Button size="sm" variant="ghost" onClick={() => startConnect(item)}>
                    {item.status === 'connected' ? 'Update secret' : 'Add secret'}
                  </Button>
                  {item.status === 'connected' ? (
                    <Button size="sm" variant="ghost" onClick={() => void disconnect(item)}>Disconnect</Button>
                  ) : null}
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => void remove(item)}>Delete</Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
